import React, { FC, useCallback } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { useExperiments, useHttpClient } from '@wix/fe-essentials-standalone';
import { Category } from '@wix/ambassador-category-services/types';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { enableInternalMarketplaceSpec } from '@utils/constants';
import { updateQueryParams } from '@utils/route';
import { useBi } from '../Hooks/useBi';
import { useShallowRouter } from '../Hooks/useRouter';
import { LocaleDataTranslations } from '@common/types';
import {
  ENABLE_CURRENCY_PREFERENCES_EXPERIMENT,
  setCurrencyPreferencesRest,
} from '@utils/currency';
import MarketplaceHeader from './MarketplaceHeader';

interface Props {
  onClick?: () => void;
  services?: Array<Category>;
  userCurrency?: string;
  sessionId?: string;
  host?: string;
  pageName?: string;
  localeDataTranslations?: LocaleDataTranslations;
  userData?: any;
  userProfileImage?: string;
  justLogo?: boolean;
}

const Header: FC<Props> = ({
  services,
  onClick,
  userCurrency,
  sessionId,
  host,
  pageName,
  localeDataTranslations,
  userData,
  userProfileImage,
  justLogo,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const logger = useBi();
  const { experiments } = useExperiments({ readOnly: true });
  const { router, navigate } = useShallowRouter();
  const httpClient = useHttpClient();
  const enableCurrencyPreferences = experiments.enabled(
    ENABLE_CURRENCY_PREFERENCES_EXPERIMENT,
  );

  const setCurrencyPreferences = async (value: string) => {
    await setCurrencyPreferencesRest({ value, httpClient });
    window.location.reload();
  };

  const onSelectCurrency = useCallback(
    (option: any) => {
      logger.report(
        exploreResultsPageGenericClick({
          clickType: 'choose currency',
          sessionId,
          data: JSON.stringify(option.id),
          exploreVersion: 'V2',
        }),
      );
      if (enableCurrencyPreferences) {
        setCurrencyPreferences(option.id);
      } else {
        navigate(
          updateQueryParams(
            {
              currency: option.id,
            },
            router,
          ),
        );
      }
    },
    [logger, navigate],
  );

  const isInternal = experiments.enabled(enableInternalMarketplaceSpec);
  const headerData = {
    services,
    keys: {
      services: t('marketplace-header-link-services'),
      join: t('marketplace-header-link-join'),
      hire: t('marketplace-header-link-hire'),
      btnLogin: t('marketplace-header-btn-login'),
      btnLogout: t('marketplace-header-btn-logout'),
      myProjects: t('marketplace-header-link-my-projects'),
      contactSupport: t('marketplace-header-link-contact-support'),
      accountSettings: t('marketplace-header-link-account-settings'),
      wix: t('marketplace-header-link-wix'),
      mobileTitle: t('marketplace-header-mobile-title'),
      mobileServiceMenuTitle: t('marketplace-header-mobile-browse-services'),
      mobileCurrencySelectionTitle: t(
        'marketplace-header-mobile-currency-selection-title',
      ),
      mobileLanguageSelectionTitle: t(
        'marketplace-header-mobile-language-selection-title',
      ),
    },
    localeDataTranslations,
    userData,
    userProfileImage,
  };

  return (
    <MarketplaceHeader
      onClick={onClick}
      headerData={headerData}
      isInternal={isInternal}
      language={language}
      pageName={pageName}
      origin={host}
      currencySelectorProps={{
        onChange: onSelectCurrency,
        value: userCurrency,
      }}
      justLogo={justLogo}
    />
  );
};

export default Header;
